import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { trpc } from '~/api/trpc';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Check, Edit, ExternalLink, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DiscountCodeAssignmentList from '../discount-codes/DiscountCodeAssignmentList';

type BundleFormData = {
    name: string;
    unitPriceGross: string;
    unitQuantity: string;
    popupEnabled: boolean;
};

const emptyFormData: BundleFormData = {
    name: '',
    unitPriceGross: '',
    unitQuantity: '',
    popupEnabled: true,
};

type StoreVariantConfig = {
    storeId: number;
    storeName: string;
    storeCity: string;
    treatmentVariantId: string | null;
    treatmentVariantName: string | null;
    treatmentVariantTreatmentId: string | null;
};

const BundlesEditor = () => {
    const navigate = useNavigate();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [editingBundleId, setEditingBundleId] = useState<string | null>(null);
    const [deletingBundleId, setDeletingBundleId] = useState<string | null>(null);
    const [formData, setFormData] = useState<BundleFormData>(emptyFormData);
    
    // Store variant configuration state
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [variantSearch, setVariantSearch] = useState('');
    const [localDiscountCodeIds, setLocalDiscountCodeIds] = useState<string[]>([]);

    const utils = trpc.useUtils();

    const { data: bundlesData, isLoading } = trpc.bookingFunnelCMS.getBundles.useQuery();
    const { data: storesData } = trpc.bookingFunnelCMS.getStoresForSelection.useQuery();
    const { data: variantsForStoreData } = trpc.bookingFunnelCMS.getTreatmentVariantsForBundleAtStore.useQuery(
        { storeId: selectedStoreId!, search: variantSearch },
        { enabled: selectedStoreId !== null && variantSearch.length >= 2 }
    );
    const { data: discountCodesData } = trpc.discountCodes.getForBundle.useQuery(
        { bundleId: editingBundleId ?? '' },
        { enabled: editingBundleId !== null }
    );

    const assignedDiscountCodeIds = (discountCodesData?.data || []).filter((code) => code.isAssigned).map((code) => code.id);
    const hasDiscountCodeChanges = discountCodesData !== undefined && (
        localDiscountCodeIds.length !== assignedDiscountCodeIds.length ||
        localDiscountCodeIds.some((id) => !assignedDiscountCodeIds.includes(id))
    );

    // Must run before the discountCodesData effect so cached data for the next bundle isn't wiped.
    useEffect(() => {
        setLocalDiscountCodeIds([]);
    }, [editingBundleId]);

    useEffect(() => {
        if (discountCodesData?.data) {
            setLocalDiscountCodeIds(discountCodesData.data.filter((code) => code.isAssigned).map((code) => code.id));
        }
    }, [discountCodesData]);

    const createMutation = trpc.bookingFunnelCMS.createBundle.useMutation({
        onSuccess: () => {
            toast.success('Bundle erstellt');
            utils.bookingFunnelCMS.getBundles.invalidate();
            setIsCreateDialogOpen(false);
            setFormData(emptyFormData);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const updateMutation = trpc.bookingFunnelCMS.updateBundle.useMutation({
        onSuccess: () => {
            toast.success('Bundle aktualisiert');
            utils.bookingFunnelCMS.getBundles.invalidate();
            setIsEditDialogOpen(false);
            setEditingBundleId(null);
            setFormData(emptyFormData);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const deleteMutation = trpc.bookingFunnelCMS.deleteBundle.useMutation({
        onSuccess: () => {
            toast.success('Bundle gelöscht');
            utils.bookingFunnelCMS.getBundles.invalidate();
            setIsDeleteDialogOpen(false);
            setDeletingBundleId(null);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const setDiscountCodesMutation = trpc.discountCodes.setForBundle.useMutation({
        onSuccess: () => {
            toast.success('Rabattcodes aktualisiert');
            utils.discountCodes.getForBundle.invalidate();
            utils.discountCodes.getOne.invalidate();
            utils.discountCodes.getAll.invalidate();
            utils.bookingFunnelCMS.getBundles.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern der Rabattcodes', { description: error.message });
        },
    });

    const updateStoreVariantMutation = trpc.bookingFunnelCMS.updateBundleStoreVariant.useMutation({
        onSuccess: (result) => {
            if (result.action === 'updated') {
                toast.success('Standort-Variante aktualisiert');
            } else {
                toast.success('Standort-Variante entfernt');
            }
            utils.bookingFunnelCMS.getBundles.invalidate();
            setVariantSearch('');
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const handleCreate = () => {
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!formData.unitPriceGross || parseFloat(formData.unitPriceGross) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Preis ein');
            return;
        }
        if (!formData.unitQuantity || parseInt(formData.unitQuantity) <= 0) {
            toast.error('Bitte geben Sie eine gültige Anzahl ein');
            return;
        }

        createMutation.mutate({
            name: formData.name.trim(),
            unitPriceGross: parseFloat(formData.unitPriceGross),
            unitQuantity: parseInt(formData.unitQuantity),
        });
    };

    const handleUpdate = () => {
        if (!editingBundleId) {
            return;
        }
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!formData.unitPriceGross || parseFloat(formData.unitPriceGross) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Preis ein');
            return;
        }
        if (!formData.unitQuantity || parseInt(formData.unitQuantity) <= 0) {
            toast.error('Bitte geben Sie eine gültige Anzahl ein');
            return;
        }

        updateMutation.mutate({
            id: editingBundleId,
            name: formData.name.trim(),
            unitPriceGross: parseFloat(formData.unitPriceGross),
            unitQuantity: parseInt(formData.unitQuantity),
            popupEnabled: formData.popupEnabled,
        });

        if (hasDiscountCodeChanges) {
            setDiscountCodesMutation.mutate({
                bundleId: editingBundleId,
                discountCodeIds: localDiscountCodeIds,
            });
        }
    };

    const handleDelete = () => {
        if (!deletingBundleId) {
            return;
        }
        deleteMutation.mutate({ id: deletingBundleId });
    };

    const handleSetStoreVariant = (bundleId: string, storeId: number, treatmentVariantId: string | null) => {
        updateStoreVariantMutation.mutate({
            bundleId,
            storeId,
            treatmentVariantId,
        });
    };

    const openEditDialog = (bundle: NonNullable<typeof bundlesData>['data'][number]) => {
        setEditingBundleId(bundle.id);
        setFormData({
            name: bundle.name,
            unitPriceGross: bundle.unitPriceGross.toString(),
            unitQuantity: bundle.unitQuantity.toString(),
            popupEnabled: bundle.popupEnabled,
        });
        setSelectedStoreId(null);
        setVariantSearch('');
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (bundleId: string) => {
        setDeletingBundleId(bundleId);
        setIsDeleteDialogOpen(true);
    };

    const bundles = bundlesData?.data || [];
    const stores = storesData?.data || [];
    const availableVariants = variantsForStoreData?.data || [];

    const formatCurrency = (value: number | string) => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
    };

    // Get store variant configs for a bundle
    const getStoreVariantConfigs = (bundle: NonNullable<typeof bundlesData>['data'][number]): StoreVariantConfig[] => {
        return stores.map((store) => {
            const storeVariant = bundle.storeVariants.find((sv) => sv.store.id === store.id);
            return {
                storeId: store.id,
                storeName: store.name,
                storeCity: store.city,
                treatmentVariantId: storeVariant?.treatmentVariant.id ?? null,
                treatmentVariantName: storeVariant?.treatmentVariant.name ?? null,
                treatmentVariantTreatmentId: storeVariant?.treatmentVariant.treatmentId ?? null,
            };
        });
    };

    // Count configured stores for a bundle
    const getConfiguredStoreCount = (bundle: NonNullable<typeof bundlesData>['data'][number]) => {
        return bundle.storeVariants.length;
    };

    // Get the current bundle being edited
    const editingBundle = bundles.find((b) => b.id === editingBundleId);
    const editingStoreConfigs = editingBundle ? getStoreVariantConfigs(editingBundle) : [];

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Bundles ermöglichen Kunden, mehrere Sitzungen zu einem reduzierten Preis zu kaufen.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neues Bundle
                </Button>
            </div>

            {bundles.length === 0 ? (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='py-12 text-center text-muted-foreground'>
                        Noch keine Bundles erstellt. Klicken Sie auf "Neues Bundle" um zu beginnen.
                    </div>
                </div>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Name</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Standorte</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Stückpreis</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Anzahl</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Gesamtpreis</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Verknüpft</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Rabattcodes</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {bundles.map((bundle) => {
                                    const totalPrice = parseFloat(bundle.unitPriceGross.toString()) * bundle.unitQuantity;
                                    const configuredCount = getConfiguredStoreCount(bundle);
                                    const totalStores = stores.length;
                                    return (
                                        <TableRow key={bundle.id} className='hover:bg-gray-50 transition-colors'>
                                            <TableCell className='font-medium'>{bundle.name || '-'}</TableCell>
                                            <TableCell>
                                                <div className='flex flex-wrap gap-1'>
                                                    {configuredCount === 0 ? (
                                                        <Badge variant='outline' className='text-xs text-amber-600 border-amber-300'>
                                                            Nicht konfiguriert
                                                        </Badge>
                                                    ) : configuredCount === totalStores ? (
                                                        stores.map((store) => (
                                                            <Badge key={store.id} variant='outline' className='text-xs'>
                                                                {store.name}
                                                            </Badge>
                                                        ))
                                                    ) : (
                                                        bundle.storeVariants.map((sv) => (
                                                            <Badge key={sv.store.id} variant='secondary' className='text-xs'>
                                                                {sv.store.name}
                                                            </Badge>
                                                        ))
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className='text-right'>
                                                {formatCurrency(bundle.unitPriceGross)}
                                            </TableCell>
                                            <TableCell className='text-right'>{bundle.unitQuantity}x</TableCell>
                                            <TableCell className='text-right font-medium'>
                                                {formatCurrency(totalPrice)}
                                            </TableCell>
                                            <TableCell className='text-center'>
                                                {bundle._count.bookingFunnelBookableItems > 0 ? (
                                                    <span className='text-sm text-green-600'>
                                                        {bundle._count.bookingFunnelBookableItems} Item(s)
                                                    </span>
                                                ) : (
                                                    <span className='text-sm text-muted-foreground'>-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className='text-center'>
                                                {bundle._count.discountCodeAssociations > 0 ? (
                                                    <span className='text-sm text-green-600'>
                                                        {bundle._count.discountCodeAssociations}
                                                    </span>
                                                ) : (
                                                    <span className='text-sm text-muted-foreground'>-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className='text-right'>
                                                <div className='flex justify-end gap-1'>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => openEditDialog(bundle)}
                                                    >
                                                        <Edit className='h-4 w-4' />
                                                    </Button>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => openDeleteDialog(bundle.id)}
                                                        disabled={bundle._count.bookingFunnelBookableItems > 0}
                                                    >
                                                        <Trash2 className='h-4 w-4' />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className='max-w-lg'>
                    <DialogHeader>
                        <DialogTitle>Neues Bundle erstellen</DialogTitle>
                        <DialogDescription>
                            Erstellen Sie ein neues Bundle. Die Treatment-Varianten pro Standort können nach der Erstellung konfiguriert werden.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <Label>Name</Label>
                            <Input
                                placeholder='z.B. 5er Karte Hydrafacial'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label>Stückpreis (€)</Label>
                                <Input
                                    type='number'
                                    step='0.01'
                                    min='0'
                                    placeholder='0.00'
                                    value={formData.unitPriceGross}
                                    onChange={(e) => setFormData({ ...formData, unitPriceGross: e.target.value })}
                                />
                                <p className='text-xs text-muted-foreground'>
                                    Preis pro Einheit im Bundle
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <Label>Anzahl Einheiten</Label>
                                <Input
                                    type='number'
                                    min='1'
                                    placeholder='5'
                                    value={formData.unitQuantity}
                                    onChange={(e) => setFormData({ ...formData, unitQuantity: e.target.value })}
                                />
                                <p className='text-xs text-muted-foreground'>
                                    Wie viele Einheiten im Bundle
                                </p>
                            </div>
                        </div>
                        {formData.unitPriceGross && formData.unitQuantity && (
                            <div className='bg-gray-50 p-3 rounded-lg'>
                                <p className='text-sm font-medium'>
                                    Gesamt: {formatCurrency(parseFloat(formData.unitPriceGross) * parseInt(formData.unitQuantity))}
                                </p>
                                <p className='text-xs text-muted-foreground'>
                                    {formData.unitQuantity}x {formData.unitPriceGross}€
                                </p>
                            </div>
                        )}
                        <div className='bg-amber-50 border border-amber-200 p-3 rounded-lg'>
                            <p className='text-sm text-amber-800'>
                                <AlertTriangle className='h-4 w-4 inline mr-1' />
                                Nach dem Erstellen müssen Sie die Treatment-Varianten für jeden Standort konfigurieren.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsCreateDialogOpen(false);
                            setFormData(emptyFormData);
                        }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCreate} disabled={createMutation.isPending}>
                            {createMutation.isPending ? 'Erstelle...' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
                setIsEditDialogOpen(open);
                if (!open) {
                    setEditingBundleId(null);
                    setFormData(emptyFormData);
                    setSelectedStoreId(null);
                    setVariantSearch('');
                }
            }}>
                <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
                    <DialogHeader>
                        <DialogTitle>Bundle bearbeiten</DialogTitle>
                        <DialogDescription>
                            Bearbeiten Sie die Bundle-Details und konfigurieren Sie die Treatment-Varianten pro Standort.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='flex-1 overflow-auto space-y-4'>
                        {/* Basic fields */}
                        <div className='space-y-2'>
                            <Label>Name</Label>
                            <Input
                                placeholder='z.B. 5er Karte Hydrafacial'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label>Stückpreis (€)</Label>
                                <Input
                                    type='number'
                                    step='0.01'
                                    min='0'
                                    placeholder='0.00'
                                    value={formData.unitPriceGross}
                                    onChange={(e) => setFormData({ ...formData, unitPriceGross: e.target.value })}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label>Anzahl Einheiten</Label>
                                <Input
                                    type='number'
                                    min='1'
                                    placeholder='5'
                                    value={formData.unitQuantity}
                                    onChange={(e) => setFormData({ ...formData, unitQuantity: e.target.value })}
                                />
                            </div>
                        </div>
                        {formData.unitPriceGross && formData.unitQuantity && (
                            <div className='bg-gray-50 p-3 rounded-lg'>
                                <p className='text-sm font-medium'>
                                    Gesamt: {formatCurrency(parseFloat(formData.unitPriceGross) * parseInt(formData.unitQuantity))}
                                </p>
                            </div>
                        )}

                        {/* Popup upsell toggle */}
                        <div className='flex items-center justify-between border rounded-lg p-3'>
                            <div className='space-y-0.5'>
                                <Label>Upsell-Popup</Label>
                                <p className='text-xs text-muted-foreground'>
                                    Wenn aktiviert, wird dieses Bundle als Upsell-Popup im Buchungsfunnel angezeigt, wenn ein Kunde eine passende Einzelbehandlung auswählt. Deaktivieren beeinflusst nicht die Sichtbarkeit des Bundles als eigenständiges Bookable Item.
                                </p>
                            </div>
                            <Switch
                                checked={formData.popupEnabled}
                                onCheckedChange={(checked) => setFormData({ ...formData, popupEnabled: checked })}
                            />
                        </div>

                        <Separator />

                        {/* Discount code assignment */}
                        <div className='space-y-3'>
                            <div>
                                <Label className='text-base font-medium'>Rabattcodes ({assignedDiscountCodeIds.length})</Label>
                                <p className='text-sm text-muted-foreground mt-1'>
                                    Wählen Sie die Rabattcodes aus, die für dieses Bundle gelten sollen — egal ob es als eigenständiges Bookable Item oder über das Upsell-Popup gekauft wird.
                                    Es werden nur Rabattcodes mit der Anwendungsebene &quot;Pro Einzelposition&quot; angezeigt —
                                    Rabattcodes auf den Gesamtbetrag gelten automatisch für alle Bundles.
                                </p>
                            </div>
                            <DiscountCodeAssignmentList
                                key={editingBundleId}
                                discountCodes={discountCodesData?.data}
                                selectedIds={localDiscountCodeIds}
                                onChange={setLocalDiscountCodeIds}
                                hasChanges={hasDiscountCodeChanges}
                            />
                        </div>

                        <Separator />

                        {/* Store variant configuration */}
                        <div className='space-y-3'>
                            <div>
                                <Label className='text-base font-medium'>Standort-Varianten</Label>
                                <p className='text-sm text-muted-foreground mt-1'>
                                    Wählen Sie für jeden Standort die passende Treatment-Variante. Standorte ohne Variante zeigen dieses Bundle nicht an.
                                </p>
                            </div>

                            {editingStoreConfigs.some((c) => !c.treatmentVariantId) && (
                                <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2'>
                                    <AlertTriangle className='h-4 w-4 text-amber-600 shrink-0 mt-0.5' />
                                    <p className='text-sm text-amber-800'>
                                        Dieses Bundle ist nicht an allen Standorten konfiguriert. Es wird nur an Standorten mit einer zugewiesenen Variante angezeigt.
                                    </p>
                                </div>
                            )}

                            <div className='space-y-2'>
                                {stores.map((store) => {
                                    const config = editingStoreConfigs.find((c) => c.storeId === store.id);
                                    const isConfigured = !!config?.treatmentVariantId;
                                    const isSearchingThisStore = selectedStoreId === store.id;

                                    return (
                                        <div key={store.id} className='border rounded-lg p-3 space-y-2'>
                                            <div className='flex items-center justify-between'>
                                                <div className='flex items-center gap-2'>
                                                    {isConfigured ? (
                                                        <Check className='h-4 w-4 text-green-600' />
                                                    ) : (
                                                        <AlertTriangle className='h-4 w-4 text-amber-500' />
                                                    )}
                                                    <span className='font-medium text-sm'>{store.name}</span>
                                                    <span className='text-xs text-muted-foreground'>{store.city}</span>
                                                </div>
                                            </div>

                                            {isConfigured ? (
                                                <div className='flex items-center gap-2 bg-green-50 px-3 py-2 rounded-md border border-green-200'>
                                                    <span
                                                        className='text-sm text-green-800 flex-1 flex items-center gap-1 cursor-pointer hover:underline'
                                                        onClick={() => config.treatmentVariantTreatmentId && config.treatmentVariantId && navigate(`/treatment/${config.treatmentVariantTreatmentId}/variants/${config.treatmentVariantId}`)}
                                                    >
                                                        {config.treatmentVariantName}
                                                        <ExternalLink className='h-3 w-3' />
                                                    </span>
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        className='h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50'
                                                        onClick={() => editingBundleId && handleSetStoreVariant(editingBundleId, store.id, null)}
                                                        disabled={updateStoreVariantMutation.isPending}
                                                    >
                                                        <X className='h-3 w-3' />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <p className='text-xs text-amber-600'>
                                                    Nicht konfiguriert - Bundle wird hier nicht angezeigt
                                                </p>
                                            )}

                                            {/* Search to set/change variant */}
                                            <div className='relative'>
                                                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                                <Input
                                                    placeholder={isConfigured ? 'Variante ändern...' : 'Variante suchen...'}
                                                    value={isSearchingThisStore ? variantSearch : ''}
                                                    onChange={(e) => {
                                                        setSelectedStoreId(store.id);
                                                        setVariantSearch(e.target.value);
                                                    }}
                                                    onFocus={() => setSelectedStoreId(store.id)}
                                                    className='pl-9 h-8 text-sm'
                                                />
                                            </div>
                                            {isSearchingThisStore && variantSearch.length >= 2 && (
                                                <div className='max-h-36 overflow-y-auto border rounded bg-white'>
                                                    {availableVariants.map((variant) => (
                                                        <div
                                                            key={variant.id}
                                                            className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                                            onClick={() => {
                                                                editingBundleId && handleSetStoreVariant(editingBundleId, store.id, variant.id);
                                                            }}
                                                        >
                                                            <p className='text-sm font-medium'>{variant.name}</p>
                                                            <p className='text-xs text-muted-foreground'>
                                                                {formatCurrency(variant.priceCents / 100)} pro Einheit
                                                            </p>
                                                        </div>
                                                    ))}
                                                    {availableVariants.length === 0 && (
                                                        <p className='text-sm text-muted-foreground py-3 text-center'>
                                                            Keine Varianten gefunden.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                            {isSearchingThisStore && variantSearch.length === 1 && (
                                                <p className='text-xs text-muted-foreground'>
                                                    Mindestens 2 Zeichen eingeben.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsEditDialogOpen(false);
                            setEditingBundleId(null);
                            setFormData(emptyFormData);
                            setSelectedStoreId(null);
                            setVariantSearch('');
                        }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleUpdate} disabled={updateMutation.isPending || setDiscountCodesMutation.isPending}>
                            {updateMutation.isPending || setDiscountCodesMutation.isPending ? 'Speichere...' : 'Speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bundle löschen</DialogTitle>
                        <DialogDescription>
                            Sind Sie sicher, dass Sie dieses Bundle löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsDeleteDialogOpen(false);
                            setDeletingBundleId(null);
                        }}>
                            Abbrechen
                        </Button>
                        <Button variant='destructive' onClick={handleDelete} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? 'Lösche...' : 'Löschen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BundlesEditor;
